import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { categoryAPI, itemAPI } from '../services/api';
import Seo, { jsonLd } from '../components/Seo';
import PublicLayout from '../components/PublicLayout';
import ProductCardMedia from '../components/ProductCardMedia';
import { ProductListSkeleton, EmptyState } from '../components/skeletons';

/**
 * Public category page at /c/:slug.
 * The slug resolves to a Category OR a SubCategory so the same URL shape
 * works across both taxonomy levels.
 *
 * Pagination model (server-side):
 *   - Items are fetched one page at a time (PAGE_SIZE per page).
 *   - Facet counts come from a dedicated endpoint that respects every
 *     active filter and includes every matching row, so the chip counts
 *     stay accurate no matter how many pages the buyer has scrolled.
 *   - Changing any filter or the search query resets to page 1 and drops
 *     the accumulated items list. Load More appends the next page.
 */

const PAGE_SIZE = 24;

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [node, setNode] = useState(null);
  const [kind, setKind] = useState(null); // 'category' | 'subCategory'
  const [resolveLoading, setResolveLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [facets, setFacets] = useState({ subCategories: [], brands: [], total: 0 });

  // Whole-filter-section collapse — session-scoped so navigating between
  // categories remembers the user's last preference.
  const [filterSectionOpen, setFilterSectionOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const v = window.sessionStorage.getItem('kj_catpage_filters_open');
    return v === null ? true : v === '1';
  });
  const toggleFilterSection = () => {
    setFilterSectionOpen(v => {
      const next = !v;
      try { window.sessionStorage.setItem('kj_catpage_filters_open', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const subFilter   = searchParams.get('sub')   || '';
  const brandFilter = searchParams.get('brand') || '';
  const searchQ     = (searchParams.get('search') || '').trim();

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  useEffect(() => { setSearchInput(searchParams.get('search') || ''); }, [searchParams]);

  // ── Resolve the slug to a Category or SubCategory document ────────────────
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      setResolveLoading(true);
      setNotFound(false);
      setNode(null);
      try {
        const cat = await categoryAPI.bySlug(slug).catch(() => null);
        if (cat?.data?.data) {
          if (cancelled) return;
          setNode(cat.data.data); setKind('category');
          return;
        }
        const sub = await categoryAPI.subBySlug(slug).catch(() => null);
        if (sub?.data?.data) {
          if (cancelled) return;
          setNode(sub.data.data); setKind('subCategory');
          return;
        }
        if (!cancelled) setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setResolveLoading(false);
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [slug]);

  // Build the item-list / facets filter object. IDs here are resolved from
  // the facet payload, which serves both _id and slug — we prefer _id for
  // server queries since the URL query param can hold either.
  const buildFilterIds = useCallback(() => {
    if (!node) return null;
    const out = {};
    if (kind === 'category')    out.categoryId    = node._id;
    if (kind === 'subCategory') out.subCategoryId = node._id;

    // Sub filter (only applies when we're on a category page)
    if (subFilter && kind === 'category') {
      const match = facets.subCategories.find(f => (f.slug || f._id) === subFilter);
      if (match?._id) out.subCategoryId = match._id;
    }
    if (brandFilter) {
      const match = facets.brands.find(f => (f.slug || f._id) === brandFilter);
      if (match?._id) out.brandId = match._id;
    }
    if (searchQ) out.search = searchQ;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, kind, subFilter, brandFilter, searchQ, facets]);

  // ── Refetch facets whenever the active filter set changes ─────────────────
  // Facets respect search + brand (for sub counts) and search + sub (for
  // brand counts), so clicking a chip immediately updates the other column.
  useEffect(() => {
    if (!node) return;
    let cancelled = false;
    const params = {};
    if (kind === 'category')    params.categoryId    = node._id;
    if (kind === 'subCategory') params.subCategoryId = node._id;
    if (searchQ) params.search = searchQ;
    // Pass active brand so sub counts exclude other brands' items (and vice
    // versa). We send brandId — the active chip's resolved _id.
    if (brandFilter) {
      const match = facets.brands.find(f => (f.slug || f._id) === brandFilter);
      if (match?._id) params.brandId = match._id;
    }
    if (subFilter && kind === 'category') {
      const match = facets.subCategories.find(f => (f.slug || f._id) === subFilter);
      if (match?._id) params.subCategoryId = match._id;
    }

    itemAPI.facets(params)
      .then(r => { if (!cancelled) setFacets(r.data.data || { subCategories: [], brands: [], total: 0 }); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?._id, kind, searchQ, subFilter, brandFilter]);

  // ── Load first page whenever slug / filter / search changes ───────────────
  useEffect(() => {
    if (!node) return;
    const filters = buildFilterIds();
    if (!filters) return;
    let cancelled = false;
    setLoadingItems(true);
    setPage(1);
    itemAPI.list({ ...filters, page: 1, limit: PAGE_SIZE })
      .then(r => {
        if (cancelled) return;
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoadingItems(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?._id, kind, subFilter, brandFilter, searchQ]);

  const loadMore = async () => {
    if (loadingMore || loadingItems) return;
    const filters = buildFilterIds();
    if (!filters) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const r = await itemAPI.list({ ...filters, page: nextPage, limit: PAGE_SIZE });
      setItems(prev => [...prev, ...(r.data.data || [])]);
      setTotal(r.data.total || total);
      setPage(nextPage);
    } catch {}
    setLoadingMore(false);
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    const q = searchInput.trim();
    if (q) next.set('search', q); else next.delete('search');
    setSearchParams(next, { replace: true });
  };

  const hasAnyFilter = subFilter || brandFilter || !!searchQ;
  const hasMore = items.length < total;

  if (resolveLoading) return (
    <PublicLayout>
      <div style={{ maxWidth: 1200, margin: '24px auto', padding: '0 24px' }}>
        <ProductListSkeleton count={6} columns={3} />
      </div>
    </PublicLayout>
  );
  if (notFound || !node) return (
    <PublicLayout>
      <div style={{ maxWidth: 600, margin: '80px auto', textAlign: 'center', padding: 24 }}>
        <Seo title="Category not found" noindex />
        <div style={{ fontSize: 56, marginBottom: 14 }}>🔍</div>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 28, marginBottom: 10 }}>Category not found</h1>
        <Link to="/catalog" style={{ color: 'var(--green)', fontWeight: 700 }}>Browse the full catalog →</Link>
      </div>
    </PublicLayout>
  );

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Catalog', url: '/catalog' },
    { name: node.name, url: `/c/${node.slug || node._id}` },
  ];
  const goItem = (item) => { window.location.href = `/item/${item.slug || item._id}`; };

  return (
    <PublicLayout>
      <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 64px)' }}>
        <Seo
          title={node.seoTitle || `${node.name} — browse and name your price`}
          description={node.seoDescription}
          canonical={`/c/${node.slug || node._id}`}
          keywords={node.seoKeywords}
          jsonLd={[
            jsonLd.breadcrumbs(breadcrumbs),
            jsonLd.itemList(items, node.name),
          ]}
        />

        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '32px 24px' }}>
          <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={b.url}>
                {i > 0 && <span style={{ margin: '0 6px' }}>›</span>}
                {i < breadcrumbs.length - 1
                  ? <Link to={b.url} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{b.name}</Link>
                  : <span style={{ color: 'var(--char)' }}>{b.name}</span>}
              </React.Fragment>
            ))}
          </nav>

          <header style={{ marginBottom: 20 }}>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 36, fontWeight: 900, color: 'var(--char)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* icon may be an emoji OR an image URL — render URLs as an <img>, never as raw text */}
              {node.icon && (node.icon.startsWith('http') || node.icon.startsWith('/uploads'))
                ? <img src={node.icon} alt="" style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                : node.icon ? <span>{node.icon}</span> : null}
              <span>{node.name}</span>
            </h1>
            {node.seoDescription && <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.65, maxWidth: 760, marginBottom: 16 }}>{node.seoDescription}</p>}

            <form
              onSubmit={handleSearch}
              role="search"
              aria-label={`Search within ${node.name}`}
              style={{ display: 'flex', gap: 8, maxWidth: 600, marginTop: 8 }}
            >
              <input
                type="search"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={`Search within ${node.name}…`}
                style={{ flex: 1, fontSize: 14, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 9, fontFamily: 'var(--font-sans)', outline: 'none', background: '#fff' }}
              />
              <button
                type="submit"
                style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
              >
                Search
              </button>
            </form>
          </header>

          {/* Filters */}
          <section aria-label="Filters" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={toggleFilterSection}
                aria-expanded={filterSectionOpen}
                aria-controls="kj-filter-body"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--char)' }}
              >
                <span style={{ display: 'inline-block', transform: filterSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--muted)' }}>▸</span>
                Filters
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>
                  · {total} item{total !== 1 ? 's' : ''}{hasAnyFilter && <> matching</>}
                </span>
              </button>
              {hasAnyFilter && (
                <button
                  onClick={clearAll}
                  style={{ background: 'none', border: 'none', color: 'var(--green)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                >
                  Clear all
                </button>
              )}
            </div>

            {filterSectionOpen && (
              <div id="kj-filter-body" style={{ marginTop: 14 }}>
                {kind === 'category' && facets.subCategories.length > 0 && (
                  <FilterRow
                    label="Sub-category"
                    facets={facets.subCategories}
                    activeKey={subFilter}
                    onPick={(k) => setFilter('sub', k)}
                  />
                )}
                {facets.brands.length > 0 && (
                  <FilterRow
                    label="Brand"
                    facets={facets.brands}
                    activeKey={brandFilter}
                    onPick={(k) => setFilter('brand', k)}
                  />
                )}
                {(kind === 'subCategory' && facets.brands.length === 0) && (
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>No brand filter available for this sub-category.</div>
                )}

                {searchQ && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                    Search: "<em style={{ fontStyle: 'normal', color: 'var(--char)' }}>{searchQ}</em>"
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Items grid */}
          {loadingItems ? (
            <ProductListSkeleton count={6} columns={3} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No items found"
              description={
                total === 0 && !hasAnyFilter
                  ? `No items in this ${kind === 'subCategory' ? 'subcategory' : 'category'} yet.`
                  : searchQ
                    ? `No items match "${searchQ}" with current filters.`
                    : 'No items match the selected filters.'
              }
              actionLabel={hasAnyFilter ? "Reset Filters" : undefined}
              onAction={hasAnyFilter ? clearAll : undefined}
            />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 18 }}>
                {items.map(item => (
                  <article key={item._id} onClick={() => goItem(item)}
                    style={{ background: '#fff', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.18s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.boxShadow = '0 10px 22px rgba(27,92,58,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ padding: '14px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.subCategoryId?.name && <span className="tag tag-green" style={{ fontSize: 10 }}>{item.subCategoryId.name}</span>}
                      {item.brandId?.name && <span className="tag" style={{ background: 'var(--gold-lt)', color: 'var(--gold-dk)', fontSize: 10 }}>{item.brandId.name}</span>}
                    </div>
                    {/* Product image */}
                    <ProductCardMedia item={item} height={180} />

                    <div style={{ padding: '6px 16px 16px' }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', lineHeight: 1.35, marginBottom: 12, minHeight: 40 }}>{item.name}</h3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Link to={`/item/${item.slug || item._id}`} onClick={e => e.stopPropagation()} className="btn btn-out btn-md" style={{ flex: 1, border: '1.5px solid var(--green)', color: 'var(--green)' }}>Details</Link>
                        <button onClick={e => { e.stopPropagation(); navigate(`/post-demand?itemId=${item._id}`); }} className="btn btn-md" style={{ flex: 1, background: 'var(--gold)', color: 'var(--char)', border: 'none', fontWeight: 700 }}>Name Price →</button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination footer */}
              <div style={{ textAlign: 'center', padding: '28px 0 16px', fontSize: 13, color: 'var(--muted)' }}>
                Showing {items.length} of {total}
              </div>
              {hasMore && (
                <div style={{ textAlign: 'center', paddingBottom: 32 }}>
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{
                      background: 'var(--green)', color: '#fff', border: 'none',
                      borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700,
                      cursor: loadingMore ? 'wait' : 'pointer', opacity: loadingMore ? 0.6 : 1,
                    }}
                  >
                    {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

// ── Filter row with per-row collapse ────────────────────────────────────────
const COLLAPSE_AFTER = 6;
function FilterRow({ label, facets, activeKey, onPick }) {
  const [expanded, setExpanded] = useState(false);
  const canCollapse = facets.length > COLLAPSE_AFTER;

  let visible = facets;
  if (canCollapse && !expanded) {
    const activeIdx = facets.findIndex(f => (f.slug || f._id) === activeKey);
    if (activeIdx >= COLLAPSE_AFTER) {
      visible = [facets[activeIdx], ...facets.slice(0, COLLAPSE_AFTER - 1)];
    } else {
      visible = facets.slice(0, COLLAPSE_AFTER);
    }
  }
  const hiddenCount = facets.length - visible.length;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label} <span style={{ opacity: 0.6, fontWeight: 500 }}>({facets.length})</span>
        </div>
        {canCollapse && (
          <button
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            style={{ background: 'none', border: 'none', color: 'var(--green)', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {expanded ? '– Collapse' : '+ Expand'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Chip active={!activeKey} onPick={() => onPick('')}>All</Chip>
        {visible.map(f => {
          const k = f.slug || f._id;
          return (
            <Chip key={k} active={activeKey === k} onPick={() => onPick(k)}>
              {f.name} <span style={{ opacity: 0.6, marginLeft: 4, fontWeight: 500 }}>({f.count})</span>
            </Chip>
          );
        })}
        {canCollapse && !expanded && hiddenCount > 0 && (
          <button
            onClick={() => setExpanded(true)}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
              border: '1.5px dashed var(--border)', background: 'transparent', color: 'var(--green)',
            }}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onPick, children }) {
  return (
    <button
      onClick={onPick}
      style={{
        padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
        border: active ? 'none' : '1.5px solid var(--border)',
        background: active ? 'var(--green)' : '#fff',
        color: active ? '#fff' : 'var(--char)',
      }}
    >
      {children}
    </button>
  );
}
